import { Injectable } from '@angular/core';

import { User } from '../models/users';

import { Firestore, doc, setDoc, collectionData, collection, query, where, getDocs, addDoc, getDoc, deleteDoc, orderBy, updateDoc, docData } from '@angular/fire/firestore';

import { Timestamp, QueryConstraint, serverTimestamp, arrayUnion } from 'firebase/firestore';

import { NewItem } from '../models/new-item';

import { Offer } from '../models/offer';

import { AuthService } from './auth.service';

import { ItemsService } from './items.service';

import { switchMap, of, Observable, combineLatest, map, catchError, from, firstValueFrom } from 'rxjs';

import { HttpClient, HttpHeaders } from '@angular/common/http';

import { environment } from 'src/environments/environment';



@Injectable({

  providedIn: 'root'

})

export class OffersService {



  constructor(

    private authService: AuthService,

    private firestore: Firestore,

    private itemsService: ItemsService,

    private http: HttpClient

  ) {}



  public makeOffer(item: NewItem, price: number) {

    return this.authService.user$.pipe(

      switchMap(user => {

        if (!user) {

          return of(null);

        }

        const offer: Offer = new Offer;

        offer.created = Timestamp.fromDate(new Date());

        offer.state = 'created';

        offer.emitterId = user.uid;

        offer.emitterName = user.displayName!;

        offer.itemId = item.id;

        offer.receiverId = item.user_id;

        offer.price = price;

        offer.currency = item.currency || 'GBP';

        

        const thumb = (item.photoUrls && item.photoUrls.length > 0)

          ? item.photoUrls[0]

          : ((item.photos && item.photos.length > 0 && typeof item.photos[0] === 'string' && item.photos[0].startsWith('http'))

              ? item.photos[0]

              : (item.id && item.user_id ? `https://storage.googleapis.com/artiquary.appspot.com/items/${item.user_id}/${item.id}/${(item.photos && item.photos.length > 0) ? item.photos[0] : 0}` : ''));

        offer.thumbnailUrl = thumb;

        offer.itemName = item.title;



        return addDoc(collection(this.firestore, 'offers'), Object.assign({}, offer));

      })

    );

  }



  public getMyOffers(): Observable<Offer[]> {

    return this.authService.user$.pipe(

      switchMap(user => {

        if (!user) {

          return of([]);

        }



        const offersCollection = collection(this.firestore, 'offers');



        const emitterQuery = query(offersCollection, where('emitterId', '==', user.uid));

        const receiverQuery = query(offersCollection, where('receiverId', '==', user.uid));

        const buyerQuery = query(offersCollection, where('buyerUid', '==', user.uid));

        const sellerQuery = query(offersCollection, where('sellerUid', '==', user.uid));



        const emitterOffers$ = (collectionData(emitterQuery, { idField: 'id' }) as Observable<Offer[]>).pipe(

          catchError(err => {

            console.warn('Emitter query notice:', err);

            return of([] as Offer[]);

          })

        );

        const receiverOffers$ = (collectionData(receiverQuery, { idField: 'id' }) as Observable<Offer[]>).pipe(

          catchError(err => {

            console.warn('Receiver query notice:', err);

            return of([] as Offer[]);

          })

        );

        const buyerOffers$ = (collectionData(buyerQuery, { idField: 'id' }) as Observable<Offer[]>).pipe(

          catchError(err => {

            console.warn('Buyer query notice:', err);

            return of([] as Offer[]);

          })

        );

        const sellerOffers$ = (collectionData(sellerQuery, { idField: 'id' }) as Observable<Offer[]>).pipe(

          catchError(err => {

            console.warn('Seller query notice:', err);

            return of([] as Offer[]);

          })

        );



        const apiOrders$ = from(this.fetchOrdersFromApi()).pipe(

          catchError(err => {

            console.warn('API orders query notice:', err);

            return of([] as Offer[]);

          })

        );



        return combineLatest([emitterOffers$, receiverOffers$, buyerOffers$, sellerOffers$, apiOrders$]).pipe(

          map(([emitterOffers, receiverOffers, buyerOffers, sellerOffers, apiOffers]) => {

            const all = [...emitterOffers, ...receiverOffers, ...buyerOffers, ...sellerOffers, ...apiOffers];

            const deduped = new Map<string, Offer>();

            all.forEach(item => {

              if (item && item.id) {

                const existing = deduped.get(item.id);

                if (existing) {

                  deduped.set(item.id, { ...existing, ...item });

                } else {

                  deduped.set(item.id, item);

                }

              }

            });

            return Array.from(deduped.values());

          })

        );

      })

    );

  }



  private async fetchOrdersFromApi(): Promise<Offer[]> {

    try {

      const user = await firstValueFrom(this.authService.user$);

      if (!user) return [];

      const token = await (user as any).getIdToken?.();

      if (!token) return [];

      const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

      const res: any = await firstValueFrom(

        this.http.get(`${environment.apiURL || ''}/api/v1/orders/my-orders`, { headers })

      );

      if (res && res.success && Array.isArray(res.orders)) {

        return res.orders as Offer[];

      }

      return [];

    } catch (err) {

      return [];

    }

  }



  public getOffersForItem(itemId: string): Observable<Offer[]> {

    const offersCollection = collection(this.firestore, 'offers');

    const q = query(offersCollection, where('itemId', '==', itemId));

    return collectionData(q, { idField: 'id' }) as Observable<Offer[]>;

  }



  public acceptOffer(offer: Offer) {

    if (offer.id) {

      const offerRef = doc(this.firestore, 'offers', offer.id);

      return updateDoc(offerRef, { state: 'accepted' });

    }

    return Promise.resolve();

  }



  public declineOffer(offer: Offer) {

    if (offer.id) {

      const offerRef = doc(this.firestore, 'offers', offer.id);

      return updateDoc(offerRef, { state: 'declined' });

    }

    return Promise.resolve();

  }



  public cancelOffer(offer: Offer) {

    if (offer.id) {

      const offerRef = doc(this.firestore, 'offers', offer.id);

      return updateDoc(offerRef, { state: 'cancelled' });

    }

    return Promise.resolve();

  }



  /**

   * Completes sale and executes the continuous provenance transfer

   */

  public async completeSaleAndTransfer(offer: Offer, notes?: string): Promise<void> {

    if (!offer.id || !offer.itemId) {

      throw new Error("Offer ID or Item ID is missing.");

    }

    const offerRef = doc(this.firestore, 'offers', offer.id);

    await updateDoc(offerRef, { state: 'completed' });



    await this.itemsService.transferOwnership(

      offer.itemId,

      offer.emitterId || '',

      offer.emitterName || 'New Collector',

      notes || `Acquired via verified offer ($${offer.price}). Ownership handed over with complete preserved provenance.`

    );

  }



  public getOffer(offerId: string): Observable<Offer> {

    const offerRef = doc(this.firestore, 'offers', offerId);

    return docData(offerRef, { idField: 'id' }) as Observable<Offer>;

  }



  /**

   * Confirms safe reception of the antique by the buyer and finalizes/closes the deal

   */

  public async confirmReception(offer: Offer, custodianName?: string): Promise<void> {

    if (!offer.id) {

      throw new Error("Offer ID is missing.");

    }



    try {

      const user = await firstValueFrom(this.authService.user$);

      if (user) {

        const token = await (user as any).getIdToken?.();

        if (token) {

          const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

          await firstValueFrom(

            this.http.post(`${environment.apiURL || ''}/api/v1/orders/${offer.id}/confirm-reception`, {}, { headers })

          );

        }

      }

    } catch (apiErr) {

      console.warn('Backend confirm-reception notice:', apiErr);

    }



    try {

      const offerRef = doc(this.firestore, 'offers', offer.id);

      await updateDoc(offerRef, {

        receptionStatus: 'confirmed',

        dealClosed: true,

        receivedAt: serverTimestamp()

      });

    } catch (clientErr) {

      console.warn('Client updateDoc confirm-reception notice:', clientErr);

    }



    offer.receptionStatus = 'confirmed';

    offer.dealClosed = true;



    if (offer.itemId) {

      const milestone = {

        id: `reception-${Date.now()}`,

        type: 'story',

        title: 'Custody Received & Transaction Closed',

        description: `New custodian verified safe arrival and inspected condition. Ownership custody permanently sealed and transaction closed in living provenance registry.`,

        author: custodianName || offer.emitterName || 'Custodian',

        date: new Date()

      };

      try {

        await this.itemsService.createEvent(offer.itemId, milestone);

      } catch (e) {

        console.warn("createEvent for reception subcollection notice:", e);

      }

      try {

        const itemDocRef = doc(this.firestore, `items/${offer.itemId}`);

        await updateDoc(itemDocRef, {

          events: arrayUnion({

            ...milestone,

            date: serverTimestamp()

          }),

          dealClosed: true,

          receptionStatus: 'confirmed'

        });

      } catch (e) {

        console.warn("updateDoc item events array notice:", e);

      }

    }

  }


  /**
   * Cancels a sale by the seller, initiates a full refund, and restores the piece to the active marketplace.
   */
  public async cancelSale(offer: Offer, reason?: string): Promise<{ success: boolean; refundStatus?: string; error?: string }> {
    if (!offer.id) {
      throw new Error("Offer ID is required.");
    }

    try {
      const user = await firstValueFrom(this.authService.user$);
      if (user) {
        const token = await (user as any).getIdToken?.();
        if (token) {
          const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
          const res: any = await firstValueFrom(
            this.http.post(`${environment.apiURL || ''}/api/v1/orders/${offer.id}/cancel-sale`, { reason }, { headers })
          );
          if (res && res.success) {
            offer.state = 'cancelled';
            offer.refundStatus = res.refundStatus;
            return res;
          }
        }
      }
    } catch (apiErr: any) {
      console.warn("Backend cancel-sale notice:", apiErr);
      const errMsg = apiErr?.error?.error || apiErr?.message || 'Failed to cancel sale via backend.';
      throw new Error(errMsg);
    }

    return { success: true };
  }
}
